import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import { BarChart, LineChart } from '@mui/x-charts';
import { formatValue } from '../../utils/utils';
import { subMonths, subWeeks, format, startOfWeek, endOfWeek } from 'date-fns';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { CHART_COLORS } from '../../constants/layout';

const CHART_MARGIN = { left: 80, right: 20, top: 40, bottom: 70 };

const MonthValueDashboard = ({ data, currency, view, setView, monthlyDataByYear, numRecords = 12, chartType = 'bar' }) => {
  const [lastTwelveMonthsData, setLastTwelveMonthsData] = useState([]);
  const [lastTwelveWeeksData, setLastTwelveWeeksData] = useState([]);
  const [showArea, setShowArea] = useState(false);
  const stickyCellSx = { position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 };
  const monthCellSx = { minWidth: 110, whiteSpace: 'nowrap' };
  const valueCellSx = { minWidth: 130, whiteSpace: 'nowrap' };

  useEffect(() => {
    if (view === 'weekly') {
      generateLastTwelveWeeksData();
    } else if (view === 'monthly' || view === 'annual') {
      generateLastTwelveMonthsData();
    }
  }, [data, currency, numRecords, view]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const generateLastTwelveWeeksData = () => {
    const weeks = [];
    const currentDate = new Date();

    for (let i = 0; i < numRecords; i++) {
      const weekDate = subWeeks(currentDate, i);
      const weekStart = startOfWeek(weekDate, { weekStartsOn: 0 }); // 0 = domingo
      const weekEnd = endOfWeek(weekDate, { weekStartsOn: 0 });
      const weekKey = `${format(weekStart, 'dd-MM-yyyy')}`;
      weeks.unshift(weekKey);
    }

    const filledData = weeks.map((weekKey) => {
      const weekData = data.find((d) => {
        const date = new Date(d.YearMonth);
        const weekStart = startOfWeek(date, { weekStartsOn: 0 });
        const weekKeyData = format(weekStart, 'dd-MM-yyyy');
        return weekKeyData === weekKey;
      });

      const currencyValue = weekData ? parseFloat(weekData.Currencies?.[currency]) : 0;

      return {
        week: weekKey,
        value: typeof currencyValue === 'number' ? currencyValue : 0,
      };
    });

    setLastTwelveWeeksData(filledData);
  };

  const generateLastTwelveMonthsData = () => {
    const months = [];
    const currentDate = new Date();

    for (let i = 0; i < numRecords; i++) {
      const monthDate = subMonths(currentDate, i);
      const yearMonth = format(monthDate, 'yyyy-MM');
      months.unshift(yearMonth);
    }

    const filledData = months.map((yearMonth) => {
      const monthData = data.find((d) => d.YearMonth === yearMonth);
      const currencyValue = monthData ? parseFloat(monthData.Currencies?.[currency]) : 0;

      return {
        year_month: yearMonth,
        value: typeof currencyValue === 'number' ? currencyValue : 0,
      };
    });

    setLastTwelveMonthsData(filledData);
  };

  const getAnnualizedData = () => {
    return (monthlyDataByYear || []).map((yearData) => {
      const { year, months } = yearData;

      if (!Array.isArray(months)) return { year, total: 0 };

      const total = months.reduce((sum, monthValue) => {
        return sum + (typeof monthValue === 'number' ? monthValue : 0);
      }, 0);

      return {
        year,
        total,
      };
    });
  };

  const renderWeeklyTableRows = () => {
    return lastTwelveWeeksData.map((row, index) => (
      <TableRow key={row.week}>
        <TableCell align="center" sx={stickyCellSx}>
          {`Semana ${index + 1}`}
        </TableCell>
        <TableCell align="center" sx={valueCellSx}>
          {formatValue(row.value, currency)}
        </TableCell>
      </TableRow>
    ));
  };

  const renderMonthlyTableRows = () => {
    return (monthlyDataByYear || []).map((row) => (
      <TableRow key={row.year}>
        <TableCell align="center" sx={stickyCellSx}>
          {row.year}
        </TableCell>
        {(Array.isArray(row.months) ? row.months : []).map((monthValue, index) => (
          <TableCell key={index} align="center" sx={monthCellSx}>
            {formatValue(monthValue || 0, currency)}
          </TableCell>
        ))}
        <TableCell align="center" sx={valueCellSx}>
          <strong>{formatValue(calculateRowTotal(row), currency)}</strong>
        </TableCell>
      </TableRow>
    ));
  };

  const renderAnnualTableRows = () => {
    const annualData = getAnnualizedData();
    return annualData.map((row) => (
      <TableRow key={row.year}>
        <TableCell align="center" sx={stickyCellSx}>
          {row.year}
        </TableCell>
        <TableCell align="center" sx={valueCellSx}>
          {formatValue(row.total, currency)}
        </TableCell>
      </TableRow>
    ));
  };

  const calculateRowTotal = (row) => {
    if (!Array.isArray(row.months)) {
      return 0;
    }
    return row.months.reduce((sum, value) => sum + (value || 0), 0);
  };

  const renderChart = () => {
    let chartData, xAxisKey, yAxisKey, label;

    if (view === 'weekly') {
      chartData = lastTwelveWeeksData;
      xAxisKey = 'week';
      yAxisKey = 'value';
      label = `Valor Semanal (${currency})`;
    } else if (view === 'annual') {
      chartData = getAnnualizedData();
      xAxisKey = 'year';
      yAxisKey = 'total';
      label = 'Total Anual';
    } else {
      chartData = lastTwelveMonthsData;
      xAxisKey = 'year_month';
      yAxisKey = 'value';
      label = `Valor Mensal (${currency})`;
    }

    if (!chartData || chartData.length === 0) {
      return (
        <Typography align="center" color="textSecondary">
          Nenhum dado disponível para exibir o gráfico.
        </Typography>
      );
    }

    return chartType === 'bar' ? (
      <BarChart
        dataset={chartData}
        xAxis={[{ dataKey: xAxisKey, scaleType: 'band' }]}
        yAxis={[{ scaleType: 'linear' }]}
        series={[{ dataKey: yAxisKey, label, color: CHART_COLORS[0] }]}
        style={{ width: '100%', height: 500 }}
        grid={{ vertical: true, horizontal: true }}
        margin={CHART_MARGIN}
      />
    ) : (
      <LineChart
        dataset={chartData}
        xAxis={[{ dataKey: xAxisKey, scaleType: 'point' }]}
        yAxis={[{ scaleType: 'linear' }]}
        series={[{ dataKey: yAxisKey, label, color: CHART_COLORS[0], strokeWidth: 3, area: showArea }]}
        style={{ width: '100%', height: 500 }}
        grid={{ vertical: true, horizontal: true }}
        margin={CHART_MARGIN}
      />
    );
  };

  return (
    <Box sx={{ width: '100%' }}>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Período:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, p: 0.5 }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 1,
                  px: 1.5,
                  gap: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                  },
                },
              }}
            >
              <Tooltip title="Visão Semanal">
                <ToggleButton value="weekly">
                  <DateRangeIcon fontSize="small" />
                  Semanal
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Visão Mensal">
                <ToggleButton value="monthly">
                  <CalendarMonthIcon fontSize="small" />
                  Mensal
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Visão Anual">
                <ToggleButton value="annual">
                  <CalendarTodayIcon fontSize="small" />
                  Anual
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ position: 'relative', minHeight: 500, mb: 3 }}>
        {renderChart()}
      </Box>

      <Box>
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table
            sx={{ minWidth: view === 'monthly' ? 1500 : 650, tableLayout: 'fixed' }}
            aria-label="table by month"
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={stickyCellSx}>
                  {view === 'weekly' ? 'Semana' : view === 'monthly' ? 'Ano' : 'Ano'}
                </TableCell>
                {view === 'weekly' && (
                  <>
                    <TableCell align="center" sx={valueCellSx}>Valor</TableCell>
                  </>
                )}
                {view === 'monthly' && (
                  <>
                    <TableCell align="center" sx={monthCellSx}>Jan</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Fev</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Mar</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Abr</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Mai</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Jun</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Jul</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Ago</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Set</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Out</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Nov</TableCell>
                    <TableCell align="center" sx={monthCellSx}>Dez</TableCell>
                    <TableCell align="center" sx={valueCellSx}>Total</TableCell>
                  </>
                )}
                {view === 'annual' && <TableCell align="center" sx={valueCellSx}>Total Anual</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {view === 'weekly' ? renderWeeklyTableRows() : view === 'monthly' ? renderMonthlyTableRows() : renderAnnualTableRows()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default MonthValueDashboard;
